import {ref} from 'vue';
import {showToast} from '~/composables/useToast';

export type NearbyCoords = { lat: number; lng: number };

const GEOLOCATION_TIMEOUT_MS = 10_000;

/**
 * Координаты пользователя для фильтра «Рядом со мной».
 * Запрашиваются только по клику: браузерный промпт на загрузке листинга не нужен.
 */
export function useNearbyLocation() {
    const coords = ref<NearbyCoords | null>(null);
    const isActive = ref(false);
    const isPending = ref(false);

    const requestCoords = (): Promise<NearbyCoords> => new Promise((resolve, reject) => {
        if (!import.meta.client || !navigator.geolocation) {
            reject(new Error('geolocation-unsupported'));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => resolve({lat: position.coords.latitude, lng: position.coords.longitude}),
            (error) => reject(error),
            {timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 5 * 60_000},
        );
    });

    const toggle = async () => {
        if (isActive.value) {
            isActive.value = false;
            return;
        }

        if (coords.value) {
            isActive.value = true;
            return;
        }

        isPending.value = true;
        try {
            coords.value = await requestCoords();
            isActive.value = true;
        } catch {
            showToast('Не удалось определить ваше местоположение', 'error');
        } finally {
            isPending.value = false;
        }
    };

    return {coords, isActive, isPending, toggle};
}
